import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";

export const DEFAULT_SEARCH_PLACEHOLDER = "Search or ask me anything";

type GlobalSearchContextValue = {
  value: string;
  setValue: (value: string) => void;
  placeholder: string;
  setPlaceholder: (placeholder: string) => void;
};

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(
  null,
);

/**
 * Wraps the router (see main.tsx), same pattern as `SecondaryHeaderProvider`
 * in secondary-header-context.tsx — lets the ONE real `<SearchInput>` the
 * shell renders (in `<RootLayout>`'s top header) be driven by whichever leaf
 * page route is currently mounted, instead of every page needing its own
 * separate local filter input next to a decorative global search box.
 */
export function GlobalSearchProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState("");
  const [placeholder, setPlaceholder] = useState(DEFAULT_SEARCH_PLACEHOLDER);
  const contextValue = useMemo(
    () => ({ value, setValue, placeholder, setPlaceholder }),
    [value, placeholder],
  );
  return (
    <GlobalSearchContext.Provider value={contextValue}>
      {children}
    </GlobalSearchContext.Provider>
  );
}

function useGlobalSearchContext(): GlobalSearchContextValue {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    throw new Error(
      "useGlobalSearch(...) must be used within a GlobalSearchProvider",
    );
  }
  return ctx;
}

/** Read by `<RootLayout>` to drive the shell's one real `<SearchInput>`. */
export function useGlobalSearchInputProps(): {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
} {
  const { value, setValue, placeholder } = useGlobalSearchContext();
  return {
    value,
    onChange: (event) => setValue(event.target.value),
    placeholder,
  };
}

/**
 * Called by a leaf page route that wants to filter its own content using
 * the shell's ONE global search box instead of a redundant local input.
 * Sets the box's placeholder to `placeholder` while mounted, and returns
 * its live text. Both the placeholder and the typed text reset on unmount
 * (route change) — the query is conceptually scoped to the page that's
 * reading it, even though the input itself is rendered by `<RootLayout>`.
 */
export function usePageSearch(placeholder: string): string {
  const { value, setPlaceholder, setValue } = useGlobalSearchContext();
  useEffect(() => {
    setPlaceholder(placeholder);
    return () => {
      setPlaceholder(DEFAULT_SEARCH_PLACEHOLDER);
      setValue("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder]);
  return value;
}

/**
 * Same shell-search-box wiring as `usePageSearch` above, but ALSO mirrors
 * the typed query into the URL under `paramKey` (read on mount, written on
 * every keystroke) — for the one page (`/workflows/active`, the only other
 * `usePageSearch` caller besides the notifications matrix, which already
 * moved its OWN filter/sort state to `useSearchParams` — see that file's
 * doc comment) whose filter is worth linking to, sharing, and surviving a
 * reload, the same reasoning the notifications page's URL-sourced filters
 * already established. Every other `usePageSearch` call (there is
 * currently exactly one: the notifications matrix, which reads `types`/
 * `channels`/`sort` from the URL itself but keeps the raw search TEXT as
 * shell-context-only, since server-side search doesn't need to be
 * shareable the way a filter/sort combination is) is completely unaffected
 * — this is an opt-in variant, not a change to the shared context's own
 * behaviour.
 */
export function usePageSearchSyncedToUrl(placeholder: string, paramKey: string): string {
  const { value, setPlaceholder, setValue } = useGlobalSearchContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // Seeds the shared search box from whatever the URL already holds (e.g. a
  // shared or reloaded `?q=ingest` link) — same mount/unmount set-and-reset
  // shape as `usePageSearch`, just with an initial value read from the URL
  // instead of always starting blank.
  useEffect(() => {
    setPlaceholder(placeholder);
    setValue(searchParams.get(paramKey) ?? "");
    return () => {
      setPlaceholder(DEFAULT_SEARCH_PLACEHOLDER);
      setValue("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount
    // only, matching `usePageSearch` above: re-running on every keystroke
    // (`searchParams`/`value` changing) would fight the write-back effect
    // below and reset the box's own live-typed value.
  }, [placeholder, paramKey]);

  // Mirrors every keystroke back into the URL — `{ replace: true }` so
  // typing doesn't spam browser history with one entry per character,
  // same convention every other URL-sourced filter in this app follows
  // (see notifications/index.tsx's own extended doc comment on why).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(paramKey, value);
        else next.delete(paramKey);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only `value`
    // (the live-typed query) should trigger a URL write; `setSearchParams`
    // is stable and `paramKey` is a caller-fixed constant.
  }, [value]);

  return value;
}
